/* eslint-disable array-callback-return */
/* eslint-disable max-len */
import React from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { findIndex } from "lodash";

import { FormContainer, CoBorrowerTitle } from "./styled";
import { AutoSaveField as Field, Checkbox } from "../../../../../../components/Form";
import { ContinueButton } from "../../../../../../components/Buttons/ContinueButton";
import { documentUnique } from "../../../../../../constants/documents";
import { RoDocument } from "../../../../../UploadDocuments/components/RoDocument/RoDocument";
import { CONTINUE } from "../../../../../../constants";
import { getRequiredMessage } from "../../../../../../utils/getValidationMessage";
import { ScrollToError } from "components/ScrollToError";

const aecbSchema = () =>
  Yup.object().shape({
    coBorrower: Yup.array().of(
      Yup.object().shape({
        consentDone: Yup.boolean()
          .oneOf([true], getRequiredMessage("Consent form held"))
          .nullable(),
        signatoryIndex: Yup.number()
      })
    )
  });

const initialValues = coBorrowers => {
  return {
    coBorrower:
      coBorrowers.length > 0
        ? coBorrowers.map(coBorrower => {
            return {
              signatoryIndex: coBorrower.index,
              consentDone: coBorrower.data.consentDone
            };
          })
        : []
  };
};

export const CoBorrowerAECBConsent = ({
  roDocuments,
  updateProspect,
  sendProspectToAPI,
  coBorrowers,
  handleContinue
}) => {
  const isCoborrowerDocumentUploaded = (documents, uniqueId, borrowerIndex) => {
    let uploaded = false;
    const docIndex = findIndex(documents, { uniqueId });
    uploaded = documents[docIndex]?.prospectDocument?.[borrowerIndex]?.uploadStatus === "Uploaded";
    return uploaded;
  };

  const checkDocuments = users => {
    let uploaded = true;
    users.forEach((borrower, borrowerIndex) => {
      const docUploaded =
        borrower.consentDone === true
          ? isCoborrowerDocumentUploaded(
              roDocuments.companyDocuments,
              documentUnique.AECB_Consent_Co_Borrower,
              borrowerIndex
            )
          : true;
      uploaded = uploaded === true ? docUploaded : false;
    });
    return uploaded;
  };

  const isConsentDocsUploaded = values => {
    const { coBorrower } = values;
    let uploaded = true;
    uploaded = uploaded ? checkDocuments(coBorrower) : false;
    return uploaded;
  };

  const consentManualUpdate = values => {
    const { coBorrower } = values;

    coBorrower.forEach(item => {
      updateProspect({
        [`prospect.orgKYCDetails.entitiesInUAE.[${item.signatoryIndex}].consentDone`]: item.consentDone
      });
    });
    sendProspectToAPI(CONTINUE).then(() => {
      handleContinue();
    });
  };

  return (
    <Formik
      initialValues={initialValues(coBorrowers)}
      onSubmit={consentManualUpdate}
      validateOnChange={false}
      validationSchema={aecbSchema}
    >
      {({ values, setFieldValue }) => {
        const isDocsUploaded = isConsentDocsUploaded(values);
        return (
          <>
            {roDocuments && (
              <FormContainer>
                <ScrollToError />
                <Grid container>
                  <Grid item sm={12} xs={12}>
                    <FieldArray name="coBorrower">
                      {arrayHelpers => (
                        <Grid item sm={12} xs={12}>
                          {coBorrowers.map((coBorrowerData, coIndex) => {
                            const { data: coBorrower, index: coBorrowerIndex } = coBorrowerData;
                            return (
                              <React.Fragment key={coBorrower.companyName}>
                                <CoBorrowerTitle variant="h5">
                                  Co Borrower - {coBorrower.companyName}
                                </CoBorrowerTitle>
                                <Grid item sm={9} xs={12}>
                                  <Field
                                    name={`coBorrower[${coIndex}].consentDone`}
                                    label="Consent form held ?"
                                    component={Checkbox}
                                    inputProps={{ tabIndex: 0 }}
                                    disabled={coBorrower.consentDone}
                                  />
                                  <Field
                                    type="hidden"
                                    value={coBorrowerIndex}
                                    name={`coBorrower[${coIndex}].signatoryIndex`}
                                  />
                                </Grid>
                              </React.Fragment>
                            );
                          })}
                        </Grid>
                      )}
                    </FieldArray>
                    <Grid item sm={12} xs={12}>
                      <RoDocument uniqueId={documentUnique.AECB_Consent_Co_Borrower} />
                    </Grid>
                  </Grid>
                  <Grid item sm={12} xs={12}>
                    <ContinueButton disabled={!isDocsUploaded} type="submit" />
                  </Grid>
                </Grid>
              </FormContainer>
            )}
          </>
        );
      }}
    </Formik>
  );
};

CoBorrowerAECBConsent.propTypes = {
  /**
   * Object containing document information for the company
   */
  roDocuments: PropTypes.shape({
    companyDocuments: PropTypes.arrayOf(
      PropTypes.shape({
        uniqueId: PropTypes.string.isRequired,
        prospectDocument: PropTypes.array
      })
    )
  }).isRequired,

  /**
   * Function to update prospect data in the Redux store
   */
  updateProspect: PropTypes.func.isRequired,

  /**
   * Function to send prospect data to the API
   */
  sendProspectToAPI: PropTypes.func.isRequired,

  /**
   * Array of co-borrower entities
   */
  coBorrowers: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number.isRequired,
      data: PropTypes.shape({
        companyName: PropTypes.string.isRequired,
        consentDone: PropTypes.bool
      }).isRequired
    })
  ).isRequired,

  /**
   * Function to handle form submission and continue to next step
   */
  handleContinue: PropTypes.func.isRequired
};
